import React, { useState, useRef, useEffect } from "react";
import {
  Grid,
  TextField,
  Button,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Chip
} from "@mui/material";
import { createTheme } from "@mui/material/styles";
import WorkIcon from "@mui/icons-material/Work";
import { styled } from "@mui/material/styles";
import BaseWrapper from "../../components/shared/BaseWrapper";
import { getJobOrders } from "../API/jobOrderAPI";
import { getTechnicians } from "../API/technicianAPI";
import { fetchAndFilterJOsBySingleDate } from "../JobOrders/utils/JobOrderFilter";

const theme = createTheme({
  palette: {
    primary: {
      main: "#1e5d45", // Custom primary color
    },
  },
});

const StyledHr = styled("hr")(({ theme }) => ({
  border: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  opacity: 0.8,
}));


function AssignTechnicians() {
  const [technicians, setTechnicians] = useState([]);
  const [jobOrders, setJobOrders] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchFilter, setSearchFilter] = useState({
    startDate: (() => {
      const now = new Date();
      const philippineTimezoneOffset = 8 * 60; // UTC+8 in minutes
      const localDate = new Date(
        now.getFullYear(),
        now.getMonth(),
        1, // First day of the current month
        0,
        0,
        0,
        0
      );
      localDate.setMinutes(localDate.getMinutes() + philippineTimezoneOffset);
      return localDate.toISOString().split("T")[0];
    })(),
  });

  const [groupedJobOrders, setGroupedJobOrders] = useState([])

  // Handle opening batch assign in new tab
  const handleAssignClick = () => {
    sessionStorage.setItem(
      "groupedJobOrders",
      JSON.stringify(groupedJobOrders)
    );

    // split out the unassigned bucket
    const unassignedBucket = groupedJobOrders.find(
      (g) => g.technicians[0].id === "unassigned"
    );
    const assignedBuckets = groupedJobOrders.filter(
      (g) => g.technicians[0].id !== "unassigned"
    );

    // Persist each one by key
    sessionStorage.setItem("assignedGroups", JSON.stringify(assignedBuckets));
    sessionStorage.setItem(
      "unassignedGroup",
      JSON.stringify(unassignedBucket || { technicians: [], orders: [] })
    );

    // Finally, open the BatchAssign page
    window.open("/batch-assign", "_blank");
  };
    

  const handleSearchButtonClick = async () => {
    try {
      setIsLoading(true);

      // Await fetching technicians data
      const techData = await getTechnicians();
      if (techData) {
        const sortedTechnicians = techData.sort((a, b) =>
          a.lastname.localeCompare(b.lastname)
        );
        setTechnicians(sortedTechnicians);
      } else {
        // In case techData is null or undefined, set an empty array
        setTechnicians([]);
      }

      // Use the shared single-day helper (already applies 05:00 → next-day 04:59 window)
      const { list } = await fetchAndFilterJOsBySingleDate({
        date: searchFilter.startDate,
        status: [],                  // include all job statuses
        serviceReportStatus: [],
        billingRecordStatus: [],
        // If need role-based technician scoping, also pass: userRoles, technicianId
        getJobOrdersApi: getJobOrders,
      });

      // Group job orders by unique combination of technicians.
      // For each job order with technicians, we generate a group key by sorting the technician IDs.
      const groupedJobOrders = list.reduce((groups, jobOrder) => {
        if (jobOrder.technicians && jobOrder.technicians.length > 0) {
          // Create a unique key from the sorted technician IDs.
          const sortedTechIds = jobOrder.technicians.map(tech => tech.id).sort();
          const groupKey = sortedTechIds.join(',');

          if (!groups[groupKey]) {
            // To represent this group, also sort the technician objects (e.g., by lastname)
            const sortedTechObjects = jobOrder.technicians.slice().sort((a, b) =>
              a.lastname.localeCompare(b.lastname)
            );
            groups[groupKey] = {
              // Store the unique combination of technicians.
              technicians: sortedTechObjects,
              orders: []
            };
          }
          groups[groupKey].orders.push(jobOrder);
        } else {
          // For job orders with no technicians, group them under "unassigned"
          if (!groups["unassigned"]) {
            groups["unassigned"] = {
              technicians: [{ id: "unassigned", name: "Unassigned" }],
              orders: []
            };
          }
          groups["unassigned"].orders.push(jobOrder);
        }
        return groups;
      }, {});

      // Convert the groups object into an array. Optionally, sort the array so that
      // the "unassigned" group comes last.
      const groupedJobOrdersArray = Object.values(groupedJobOrders).sort((a, b) => {
        if (a.technicians[0].id === "unassigned") return 1;
        if (b.technicians[0].id === "unassigned") return -1;
        // Otherwise, sort by the concatenation of technician last names.
        const aNames = a.technicians.map(tech => tech.lastname).join(',');
        const bNames = b.technicians.map(tech => tech.lastname).join(',');
        return aNames.localeCompare(bNames);
      });

      // Save the grouped job orders to state
      setGroupedJobOrders(groupedJobOrdersArray);
      setError(null); // Clear any previous errors
    } catch (err) {
      setError(err.message);
      setErrorMessage("Failed to fetch Job Orders. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const FIXED_CELL_WIDTH = 150; // px for each order cell

    // Count orders per group for header
  const ordersCountByKey = groupedJobOrders.reduce((acc, g) => {
    const key = g.technicians
      .map((t) => t.id)
      .sort()
      .join("-");
    acc[key] = g.orders.length;
    return acc;
  }, {});


  return (
    <BaseWrapper
      titleIcon={<WorkIcon />}
      title="Operations"
      subTitle="Technician Assignment"
      subTitleRoute="/assign-technicians"
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={12} lg={2}>
          <TextField
            label="Date"
            type="date"
            variant="outlined"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={searchFilter.startDate}
            onChange={(e) =>
              setSearchFilter((prev) => ({
                ...prev,
                startDate: e.target.value,
              }))
            }
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          md={2}
          container
          justifyContent={{
            xs: "center",
            sm: "center",
            md: "flex-start",
            lg: "flex-start",
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearchButtonClick}
            disabled={isLoading}
          >
            Search
          </Button>
        </Grid>
      </Grid>      

      <StyledHr />

      {/* Assign Technicians Button */}
      {groupedJobOrders.length > 0 && (
        <Grid container justifyContent="flex-end" style={{ marginTop: 16, marginBottom: 8 }}>
          <Grid item>
            <Button variant="contained" color="primary" onClick={handleAssignClick}>
              ASSIGN TECHNICIANS
            </Button>
          </Grid>
        </Grid>
      )}

      {/* Schedule Table */}
      <Grid item xs={12}>
        {(() => {
          // Set the height for each minute row (adjust as needed)
          const cellHeight = 1; // in pixels

          // Helper to format a "HH:mm" time string into AM/PM format.
          const formatAmPm = (timeStr) => {
            const [hStr, mStr] = timeStr.split(":");
            let hour = parseInt(hStr, 10);
            const minute = mStr;
            const ampm = hour >= 12 ? "PM" : "AM";
            hour = hour % 12;
            if (hour === 0) hour = 12;
            return `${hour}:${minute} ${ampm}`;
          };

          // Create an array of time slots (each minute) starting at 4:00 AM for 1560 minutes (26 hours).
          const timeSlots = [];
          for (let i = 0; i < 1560; i++) {
            const totalMinutes = 4 * 60 + i;
            const hour = Math.floor(totalMinutes / 60) % 24;
            const minute = totalMinutes % 60;
            const rawTime =
              hour.toString().padStart(2, "0") +
              ":" +
              minute.toString().padStart(2, "0");
            timeSlots.push(formatAmPm(rawTime));
          }

          // Helper to convert a time string ("HH:mm") to total minutes (adjusting for times before 4:00 AM).
          const convertTime = (timeStr) => {
            const [hStr, mStr] = timeStr.split(":");
            let h = parseInt(hStr, 10);
            const m = parseInt(mStr, 10);
            let totalMinutes = h * 60 + m;
            if (totalMinutes < 4 * 60) {
              totalMinutes += 24 * 60;
            }
            return totalMinutes;
          };

          // Helper: group orders into subgroups of non-overlapping intervals.
          const groupOrdersIntoSubgroups = (orders) => {
            const sorted = orders.slice().sort(
              (a, b) => convertTime(a.start_time) - convertTime(b.start_time)
            );
            const subgroups = [];
            sorted.forEach((order) => {
              let placed = false;
              for (let subgroup of subgroups) {
                const lastOrder = subgroup[subgroup.length - 1];
                if (convertTime(order.start_time) >= convertTime(lastOrder.end_time)) {
                  subgroup.push(order);
                  placed = true;
                  break;
                }
              }
              if (!placed) {
                subgroups.push([order]);
              }
            });
            if (subgroups.length > 1) {
              subgroups.sort((a, b) => {
                const avgA =
                  a.reduce((sum, o) => sum + convertTime(o.start_time), 0) / a.length;
                const avgB =
                  b.reduce((sum, o) => sum + convertTime(o.start_time), 0) / b.length;
                return avgB - avgA;
              });
            }
            return subgroups;
          };

          // Process orders into columns (each subgroup becomes its own column)
          const processedColumns = [];
          groupedJobOrders.forEach((group) => {
            const orders = group.orders || [];
            const subgroups = orders.length > 0 ? groupOrdersIntoSubgroups(orders) : [[]];
            subgroups.forEach((subgroup) => {
              const slots = new Array(timeSlots.length).fill(null);
              subgroup.forEach((order) => {
                const startMinutes = convertTime(order.start_time);
                const endMinutes = convertTime(order.end_time);
                const startRow = startMinutes - 4 * 60;
                const endRow = endMinutes - 4 * 60;
                const span = endRow - startRow;
                if (startRow >= 0 && startRow < slots.length) {
                  if (!slots[startRow]) {
                    slots[startRow] = { order, rowSpan: span };
                    for (let i = startRow + 1; i < endRow && i < slots.length; i++) {
                      slots[i] = "occupied";
                    }
                  }
                }
              });
              processedColumns.push({
                technicians: group.technicians,
                slots,
              });
            });
          });

          // Merge header cells for columns that belong to the same technician group.
          const headerGroupsMap = {};
          const headerGroupsArr = [];
          
          processedColumns.forEach((col, index) => {
            const key = col.technicians
              .map((tech) => tech.id)
              .sort()
              .join("-");
            if (!headerGroupsMap[key]) {
              headerGroupsMap[key] = { key, technicians: col.technicians, indices: [] };
              headerGroupsArr.push(headerGroupsMap[key]);
            }
            headerGroupsMap[key].indices.push(index);
          });

          const groupIndexByCol = processedColumns.map((_, colIndex) =>
            headerGroupsArr.findIndex(g => g.indices.includes(colIndex))
          );

          return processedColumns && processedColumns.length > 0 ? (() => {
            // total width of the table in px
            const totalWidth = processedColumns.length * FIXED_CELL_WIDTH;

            return (
              <>
                <TableContainer
                  component={Paper}
                  style={{ 
                    overflowY: "auto",
                    overflowX: "auto",
                    padding: 0, 
                    margin: 0,
                    maxHeight: "calc(100vh - 200px)",
                  }}
                >                  
                  <Table
                    size="small"
                    style={{
                      borderCollapse: "collapse",
                      tableLayout: "auto",
                      width: "auto",
                      minWidth: `${100 + processedColumns.length * 150}px`, // Ensure minimum width based on columns
                    }}
                  >
                    <TableHead>                      
                      <TableRow>
                        <TableCell
                          align="left"
                          style={{
                            fontSize: "0.75rem",
                            width: "100px",
                            minWidth: "100px",
                            maxWidth: "100px",
                            whiteSpace: "normal", // allow text wrap
                            fontWeight: "bold",
                            borderRight: "2px solid #ccc",
                            height: `${cellHeight}px`,
                            position: "sticky",
                            left: 0, // Make the Time column sticky
                            backgroundColor: "#fff", // Background color to prevent overlap
                            zIndex: 2, // Ensure it stays on top
                          }}
                        >
                          Time
                        </TableCell>
                        {headerGroupsArr.map((group, idx) => {
                          const isUnassigned = group.key === "unassigned";
                          const key = group.technicians.map(t => t.id).sort().join("-");
                          const totalJobOrders = ordersCountByKey[key] || 0;

                          return (
                            <TableCell
                              key={key}
                              align="left"
                              colSpan={group.indices.length}
                              style={{
                                fontSize: "0.85rem",
                                padding: "4px",
                                borderBottom: "1px solid #ccc",
                                borderRight: "1px solid #ccc",
                                whiteSpace: "normal", // allow text wrap
                                backgroundColor: isUnassigned ? "gray" : "white",
                                color:           isUnassigned ? "white" : "inherit",
                                fontWeight: "bold",
                                minWidth: `${150 * group.indices.length}px`,
                                maxWidth: `${150 * group.indices.length}px`,
                              }}
                            >
                              {isUnassigned ? (
                                <>
                                  <Typography variant="subtitle2">Unassigned</Typography>
                                  <Typography variant="caption">
                                    Total: {totalJobOrders}
                                  </Typography>
                                </>
                              ) : (
                                <>
                                  {/* show team number above the list */}
                                  <Typography variant="caption">
                                    Team {idx + 1}
                                  </Typography>                              

                                  {/* the white-background list */}
                                  <ol style={{ 
                                    margin: 0, 
                                    paddingLeft: "1em", 
                                    backgroundColor: "white" 
                                  }}>
                                    {group.technicians.map((tech, i) => (
                                      <li key={tech.id} style={{ fontWeight: "bold" }}>
                                        {tech.firstname} {tech.lastname}
                                      </li>
                                    ))}
                                  </ol>

                                  {/* show total job orders */}
                                  <Typography variant="caption">
                                    Total Jobs: {totalJobOrders}
                                  </Typography>
                                </>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {timeSlots.map((timeLabel, rowIndex) => (
                        <TableRow
                          key={rowIndex}
                          style={{ padding: 0, margin: 0, borderBottom: "none" }}
                        >
                          {rowIndex % 60 === 0 && (
                            <TableCell
                              align="left"
                              rowSpan={60} // Merge 60 rows for the full hour
                              style={{
                                fontSize: "0.75rem",
                                width: "100px",
                                minWidth: "100px",
                                maxWidth: "100px",      
                                height: `${cellHeight * 60}px`, // height for one full hour
                                whiteSpace: "normal", // allow text wrap
                                verticalAlign: "top",
                                borderRight: "2px solid #ccc",
                                position: "sticky",
                                left: 0, // Make the Time column sticky
                                backgroundColor: "#fff", // Background color to prevent overlap
                                zIndex: 1, // Keep it below the header but above the body
                              }}
                            >
                              {timeSlots[rowIndex]}
                            </TableCell>
                          )}
                          {processedColumns.map((col, colIndex) => {
                            const cellData = col.slots[rowIndex];
                            // Define extra style for full hour rows (when time is :00)
                            const extraStyle = rowIndex % 60 === 0 ? { borderTop: "1px solid #ccc" } : {};

                            if (cellData === "occupied") {
                              return null;
                            } else if (cellData && typeof cellData === "object") {
                              const isAssigned = col.technicians && col.technicians[0].id !== "unassigned";
                              return (
                                <TableCell
                                  key={colIndex}
                                  align="left"
                                  rowSpan={cellData.rowSpan}
                                  style={{
                                    padding: 0,
                                    margin: 0,
                                    height: `${cellHeight * cellData.rowSpan}px`,
                                    verticalAlign: "top",
                                    ...extraStyle,
                                    whiteSpace: "normal", // allow text wrap
                                    width: `${FIXED_CELL_WIDTH}px`,
                                    minWidth: `${FIXED_CELL_WIDTH}px`,
                                    maxWidth: `${FIXED_CELL_WIDTH}px`,
                                    borderRight: "1px solid #ccc",
                                    backgroundColor: isAssigned
                                      ? 'rgba(0, 149, 32, 0.6)'
                                      : 'rgba(0, 149, 32, 0.4)'                                    
                                  }}
                                >
                                  <div
                                    style={{
                                      textAlign: "left",
                                      whiteSpace: "normal", // allow text wrap
                                      padding: 0,
                                      margin: 0,
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "0.7rem",
                                        fontWeight: "bold",
                                        marginTop: "4px"
                                      }}
                                    >
                                      {formatAmPm(cellData.order.start_time)} - {formatAmPm(cellData.order.end_time)}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "0.75rem",
                                        fontWeight: 550,
                                        color: "rgba(0, 0, 0, 0.65)",
                                        cursor: "pointer" 
                                      }}
                                      onClick={() => window.open(`/edit-job-order/${cellData.order.id}`, "_blank")}
                                    >
                                      {cellData.order.client.name}
                                    </div>
                                    {cellData.order.job_types && (
                                      <>
                                        {/* show up to first 2 types */}
                                        {cellData.order.job_types.slice(0, 2).map(type => {
                                          const formatted = type.replace(/_/g, ' ');
                                          return (
                                            <Chip
                                              key={type}
                                              label={formatted}
                                              size="small"
                                              sx={{ mt: 0.5, mr: 0.5 }}
                                            />
                                          );
                                        })}
                                        {/* if more than 3, show “+N other…” */}
                                        {cellData.order.job_types.length > 2 && (
                                          <Chip
                                            key="more"
                                            label={`+${cellData.order.job_types.length - 2} other...`}
                                            size="small"
                                            sx={{ mt: 0.5, mr: 0.5 }}
                                          />
                                        )}
                                      </>
                                    )}
                                    {cellData.order.work_permit_required && (
                                      <div style={{ marginTop: 6, display: "flex", justifyContent: "flex-end" }}>
                                        <Chip
                                          label="WORK PERMIT REQUIRED"
                                          size="small"
                                          variant="outlined"
                                          sx={{
                                            borderColor: "rgba(25,118,210,0.5)",
                                            color: "#1976d2",
                                            bgcolor: "rgba(25,118,210,0.06)",
                                            fontWeight: 550,
                                            height: 15,
                                            fontSize: "0.65rem",
                                            letterSpacing: "0.2px",
                                            "& .MuiChip-label": { px: 0.75 },
                                          }}
                                        />
                                      </div>
                                    )}

                                    <div
                                      style={{
                                        fontSize: "0.75rem",
                                        marginBottom: "4px",
                                        fontWeight: 550,
                                      }}
                                    >
                                      {cellData.order.client.address.city}
                                    </div>
                                  </div>
                                </TableCell>
                              );
                            } else {
                              // pick a slightly darker vs white based on even/odd group
                              const bg = groupIndexByCol[colIndex] % 2 === 0
                                ? "rgba(0,0,0,0.04)"
                                : "#fff";
                              return (
                                <TableCell
                                  key={colIndex}
                                  align="left"
                                  style={{
                                    fontSize: "0.75rem",
                                    padding: 0,
                                    margin: 0,
                                    height: `${cellHeight}px`,
                                    whiteSpace: "normal",
                                    border: "none",
                                    borderRight: "1px solid #ccc",
                                    ...extraStyle,
                                    width: `${FIXED_CELL_WIDTH}px`,
                                    minWidth: `${FIXED_CELL_WIDTH}px`,
                                    maxWidth: `${FIXED_CELL_WIDTH}px`,
                                    backgroundColor: bg,
                                  }}
                                />
                              );
                            }
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>                  
                </TableContainer>
              </>
            );
          })() : null;
        })()}
      </Grid>

    </BaseWrapper>

  );
}

export default AssignTechnicians;
